@if (selectedFolder(); as folder) {
  <div class="folder-header" [class.sub-folder-header]="!!folder.parentId">
    <button nbButton (click)="goBack()" ghost status="primary" class="back-button">
      <nb-icon icon="arrow-back-outline" pack="eva"></nb-icon>
    </button>
    @if (isSubFolderSelected() && selectedRootFolder(); as rootFolder) {
      <nb-icon icon="folder" pack="eva" [style.color]="rootFolder.color"></nb-icon>
      <div class="folder-name breadcrumb-link" tabindex="0" (click)="selectFolder(rootFolder)"
           (keydown.enter)="selectFolder(rootFolder)">{{ rootFolder.name }}</div>
      <nb-icon icon="chevron-right-outline" pack="eva" class="breadcrumb-separator"></nb-icon>
    }
    <nb-icon [icon]="folder.parentId ? 'folder-outline' : 'folder'" pack="eva" [style.color]="folder.color"></nb-icon>
    <div class="folder-name">{{ folder.name }}</div>
    <wr-private-folder-badge [publicFolder]="folder.publicFolder"></wr-private-folder-badge>
    <div class="flex-grow"></div>
    @if (folder.parentId && !folder.attachmentFolder && allowSubFolderManagement()) {
      <button nbButton ghost status="primary" (click)="renameSubFolder.emit(folder)"
              nbTooltip="Unterordner umbenennen" nbTooltipPlacement="bottom">
        <nb-icon icon="edit-outline" pack="eva"></nb-icon>
      </button>
      <button nbButton ghost status="danger" (click)="deleteSubFolder.emit(folder)"
              nbTooltip="Unterordner löschen" nbTooltipPlacement="bottom">
        <nb-icon icon="trash-2-outline" pack="eva"></nb-icon>
      </button>
    }
  </div>

  @if (!folder.parentId) {
    <div class="sub-folder-section">
      @if (folder.subFolders?.length || allowSubFolderManagement()) {
        <div class="section-header">
          <div class="section-title">Unterordner</div>
          @if (allowSubFolderManagement()) {
            <button nbButton outline status="primary" size="small" (click)="createSubFolder.emit(folder)">
              <nb-icon icon="plus-outline" pack="eva"></nb-icon>
              Unterordner anlegen
            </button>
          }
        </div>
      }
      <div class="folders">
        @for (subFolder of folder.subFolders ?? []; track subFolder.id) {
          <div class="folder" tabindex="0" (click)="selectFolder(subFolder)" (keydown.enter)="selectFolder(subFolder)">
            <nb-icon icon="folder-outline" pack="eva" [style.color]="subFolder.color"></nb-icon>
            <div class="folder-name">{{ subFolder.name }}</div>
            @if (documentCount(subFolder) > 0) {
              <div class="folder-content-length">{{ documentCount(subFolder) }} Dokumente</div>
            } @else {
              <div class="folder-content-length">leer</div>
            }
          </div>
        } @empty {
          <div class="empty-hint">Keine Unterordner vorhanden.</div>
        }
      </div>
    </div>
  }

  <div class="section-title">Dokumente</div>
  <div class="folder-content">
    <ng-container *ngTemplateOutlet="fileList; context: { $implicit: folder }"></ng-container>
    @if (!disabledSignal()) {
      <div class="attachment-drop-zone">
        <wr-attachment-upload-section
          [formControlContext]="getContextForInstance(folder)"></wr-attachment-upload-section>
      </div>
    }
  </div>
} @else {
  @if (showRootDocuments()) {
    <div class="section-title">Ordner</div>
  }
  <div class="folders">
    @if (folders().length > 0) {
      @for (folder of folders(); track folder.id) {
        <div class="folder" tabindex="0" (click)="selectFolder(folder)" (keydown.enter)="selectFolder(folder)">
          <nb-icon icon="folder" pack="eva" [style.color]="folder.color"></nb-icon>
          <div class="folder-name">{{ folder.name }}</div>
          <wr-private-folder-badge [publicFolder]="folder.publicFolder"></wr-private-folder-badge>
          @if (subFolderCount(folder) > 0) {
            <div class="folder-content-length">{{ subFolderCount(folder) }} Unterordner</div>
          }
          @if (documentCount(folder) > 0) {
            <div class="folder-content-length">{{ documentCount(folder) }} Dokumente</div>
          }
          @if (folderIsEmpty(folder)) {
            <div class="folder-content-length">leer</div>
          }
        </div>
      }
    } @else {
      <div class="no-folders">Es sind keine Ordner verfügbar. Es müssen erst Ordner für diesen Tenant angelegt werden.
      </div>
    }
  </div>
  @if (showRootDocuments()) {
    <div class="root-documents">
      <div class="section-title">Dokumente</div>
      <ng-container *ngTemplateOutlet="fileList; context: { $implicit: undefined }"></ng-container>
      @if (!disabledSignal()) {
        <div class="attachment-drop-zone">
          <wr-attachment-upload-section [formControlContext]="getRootContext()"></wr-attachment-upload-section>
        </div>
      }
    </div>
  }
}

<ng-template #fileList let-containingFolder>
  @if (attachmentsWithProfilePictures()?.length === 0) {
    <div class="empty-hint">Keine Dokumente vorhanden.</div>
  }
  <nb-list>
    @for (group of attachmentYearGroups(); track group.year) {
      <ng-container ngProjectAs="nb-list-item">
        <nb-list-item class="year-header">
          <div class="year-label">{{ group.year ?? 'Ohne Datum' }}</div>
          <div class="year-rule"></div>
        </nb-list-item>
        @for (attachment of group.attachments; track attachment.s3key) {
          <nb-list-item>
            <div class="file-row">
              <div class="icon">
                <nb-icon [icon]="fileTypeIconRecord[attachment.type]" pack="eva"></nb-icon>
              </div>

              <div class="flex-elements">
                <a class="filename" href="{{attachment.presignedUrl}}" target="_blank">
                  <span>{{ getFullFileName(attachment) | ellipsis:50 }}</span>
                </a>

                <div class="created-information">
                  @if (attachment.fullName !== '') {
                    <div class="creator-info">
                      @if (attachment.creatorId) {
                        <nb-user [picture]="attachment.profilePicture!!" [name]="attachment.fullName" class="creator"
                                 [size]="'small'"></nb-user>
                      }
                    </div>
                  }
                  @if (attachment.created) {
                    <div class="timestamp">{{ attachment.created | relativeDate }}</div>
                  }
                </div>
              </div>

              <wr-short-tag-list [tags]="attachment.tags"></wr-short-tag-list>
              <div class="flex-grow"></div>

              <div class="comment-slot">
                @if (attachment.comment.length) {
                  <nb-icon class="comment-info"
                           icon="message-circle-outline"
                           pack="eva"
                           nbTooltip="{{attachment.comment}}"></nb-icon>
                }
              </div>
              <button nbButton
                      ghost
                      (click)="openAttachment(attachment)"
                      nbTooltip="Download">
                <nb-icon icon="download-outline" pack="eva"></nb-icon>
              </button>
              @if (!disabledSignal() && allowAttachmentEditing()) {
                <button nbButton
                        ghost
                        status="primary"
                        (click)="editAttachment(attachment, containingFolder)"
                        nbTooltip="Bearbeiten">
                  <nb-icon icon="edit-outline" pack="eva"></nb-icon>
                </button>
                <button nbButton
                        ghost
                        status="danger"
                        (click)="deleteAttachment(attachment, containingFolder)"
                        nbTooltip="Löschen">
                  <nb-icon icon="trash-2-outline" pack="eva"></nb-icon>
                </button>
              }
            </div>
          </nb-list-item>
        }
      </ng-container>
    }
  </nb-list>
</ng-template>
